/**
 * Header and footer, drawn on the canvas as the site draws them.
 *
 * Mirrors `SiteHeader.tsx` / `SiteFooter.tsx` in `zoea-website-module`: same
 * class names, same structural decisions, same `canGoTransparent` safety
 * rule. `sites-elements.css` (the mirrored stylesheet) paints both.
 *
 * The differences are editorial, and each is deliberate:
 *
 * - Nothing navigates. Links render as `<span>`, so a click cannot eject the
 *   owner from the Builder. Suppressing an `<a>` with preventDefault would
 *   still leave middle-click and "open in new tab" live.
 * - Clicking the header or footer selects it and opens its inspector.
 * - The mobile drawer is driven by editor state rather than published-site
 *   navigation, because the canvas must keep the owner inside the Builder.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { FreeformGeometry, GridPlacement, MediaTarget, SectionStyle, SiteChrome, SitePage, ViewportKey } from '../types';
import {
  FOOTER_FREEFORM_DEFAULT_ELEMENT_HEIGHT,
  configuredFooterMinimumHeight,
  fallbackFreeformGeometry,
  footerGridGuides,
  geometryForViewport,
  legacyPlacementForMigration,
  layerToZIndex,
  logicalDelta,
  measuredFreeformGeometry,
  resolveFooterRenderedHeight,
} from '../freeformFooter';
import { calculateAlignmentGuides } from '../guides';
import type { AlignmentGuide, GuideRect } from '../guides';
import { CanvasIcon } from './CanvasIcon';
import { resolveButton, resolveDestination, resolveVisualSurface } from './tokens';

type Theme = SiteChrome['theme'];
type CanvasFooterElement = NonNullable<SiteChrome['footer']['columns']>[number]['elements'][number];

/** Stable id used while an older flat footer is being upgraded in the UI. */
export const FOOTER_COPYRIGHT_ELEMENT_ID = 'footer-copyright';
const FOOTER_CONTENT_MEASURE_EPSILON = 0.25;

/**
 * Mirror of `canGoTransparent`. Transparent navigation renders white, so it
 * needs something dark beneath it; anything else falls back to a solid
 * surface rather than producing white-on-white links.
 */
export function canGoTransparent(style: SectionStyle | undefined): boolean {
  if (!style) return false;
  if (style.colorScheme === 'dark') return true;
  const mode = style.background?.mode;
  return mode === 'gradient' || mode === 'image';
}

interface NavigationGroup {
  page: SitePage;
  children: SitePage[];
}

function navigationGroups(pages: SitePage[]): NavigationGroup[] {
  const included = pages
    .filter((page) => page.navigation?.include !== false)
    .sort((a, b) => (a.navigation?.order ?? 0) - (b.navigation?.order ?? 0));
  const includedIds = new Set(included.map((page) => page.pageId));
  const childrenByParent = new Map<string, SitePage[]>();

  included.forEach((page) => {
    const parentId = page.navigation?.parentPageId;
    if (!parentId || !includedIds.has(parentId) || parentId === page.pageId) return;
    const children = childrenByParent.get(parentId) ?? [];
    children.push(page);
    childrenByParent.set(parentId, children);
  });

  return included
    .filter((page) => {
      const parentId = page.navigation?.parentPageId;
      return !parentId || !includedIds.has(parentId) || parentId === page.pageId;
    })
    .map((page) => ({ page, children: childrenByParent.get(page.pageId) ?? [] }));
}

interface ChromeProps {
  site: SiteChrome | null;
  pages: SitePage[];
  currentPageId: string | null;
  /** Mobile viewport shows the drawer trigger instead of the link row. */
  mobile: boolean;
  selected: boolean;
  onSelect: () => void;
  onOpenInspector?: () => void;
  onOpenMedia?: (target: MediaTarget) => void;
  mediaUrl?: (assetId: string) => string;
}

export function CanvasHeader({
  site,
  pages,
  currentPageId,
  mobile,
  selected,
  onSelect,
  onOpenInspector,
  onOpenMedia,
  mediaUrl,
  allowTransparent,
}: ChromeProps & { allowTransparent: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdownPageId, setOpenDropdownPageId] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLElement | null>(null);
  const previousOverflowRef = useRef<string | null>(null);
  const header = site?.header;
  const sticky = header?.behavior === 'sticky' || header?.behavior === 'transparent-sticky';
  const menuStyle = header?.mobileMenuStyle ?? 'dropdown';

  useEffect(() => {
    if (!sticky || typeof window === 'undefined') return;
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sticky]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setOpenDropdownPageId(null);
    window.requestAnimationFrame(() => menuButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const focusable = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? []);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (menuStyle !== 'dropdown' && !menuRef.current?.contains(event.target as Node) && !menuButtonRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    const lockBody = menuStyle !== 'dropdown';
    if (lockBody) {
      previousOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      if (lockBody) {
        document.body.style.overflow = previousOverflowRef.current ?? '';
        previousOverflowRef.current = null;
      }
    };
  }, [closeMenu, menuOpen, menuStyle]);

  if (!site || !header) return null;
  const { settings, theme } = site;
  const transparentMode = header.behavior === 'transparent' || header.behavior === 'transparent-sticky';
  const transparent = transparentMode && allowTransparent && !(header.behavior === 'transparent-sticky' && scrolled);

  const navigation = navigationGroups(pages);

  const className = [
    'zs-header',
    `zs-header-${header.layout}`,
    transparent ? 'zs-header-transparent' : header.surface === 'dark' ? 'zs-header-dark' : 'zs-header-solid',
    sticky ? 'zs-header-sticky' : '',
    scrolled && header.behavior === 'transparent-sticky' ? 'zs-header-scrolled' : '',
    scrolled && header.behavior === 'transparent-sticky' ? `zs-header-scrolled-shadow-${header.stickyShadow ?? 'subtle'}` : '',
    menuOpen ? 'zs-header-menu-open' : '',
    selected ? 'ring-1 ring-inset ring-teal-400/70' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header
      className={className}
      style={{ ...(transparent ? {} : header.backgroundColor ? { backgroundColor: header.backgroundColor } : {}), ...(header.textColor ? { color: header.textColor } : {}), ...(scrolled && header.behavior === 'transparent-sticky' ? { backgroundColor: header.stickyBackgroundColor ?? header.backgroundColor ?? undefined, color: header.stickyTextColor ?? header.textColor ?? undefined } : {}) }}
      onClick={(event) => { event.stopPropagation(); onSelect(); }}
      role="button"
      tabIndex={0}
      aria-label="Header bewerken"
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(); } }}
    >
      {selected ? <div className="relative z-20 mx-3 mb-2 flex w-fit items-center gap-1 rounded-xl border border-slate-200 bg-white p-1.5 text-slate-800 shadow-lg" onClick={(event) => event.stopPropagation()} role="toolbar" aria-label="Header-acties"><span className="px-2 text-xs font-bold">Header</span><button type="button" onClick={onOpenInspector} className="rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-700">Ontwerp</button></div> : null}
      <div className="zs-header-inner">
        <span className="zs-brand">
          {header.logoAssetId ? <button type="button" className="zs-logo-placeholder zs-logo-image-button cursor-pointer" aria-label="Headerlogo wijzigen" onClick={(event) => { event.stopPropagation(); onOpenMedia?.({ kind: 'header-logo' }); }}><img src={mediaUrl?.(header.logoAssetId)} alt="" className="zs-logo-image" /></button> : null}
          {header.showSiteName || !header.logoAssetId ? <span>{settings.siteName || 'Onze gemeente'}</span> : null}
        </span>

        {/* Both are always rendered, exactly as the published site renders
            them, and the stylesheet decides which is visible at the current
            canvas width. Branching on the viewport prop here made React and
            CSS disagree about what "mobile" means: the container query hid
            the nav below 860px while React only produced the toggle at the
            Mobile setting, so a narrow Desktop canvas showed neither. */}
        <button ref={menuButtonRef} type="button" className={`zs-nav-button zs-nav-button-${header.mobileMenuButton ?? 'lines'}`} aria-label={menuOpen ? 'Menu sluiten' : 'Menu openen'} aria-haspopup="menu" aria-expanded={menuOpen} aria-controls="zs-canvas-mobile-navigation" onClick={(event) => { event.stopPropagation(); setMenuOpen((current) => !current); }}>
          {(header.mobileMenuButton ?? 'lines') === 'label' ? (menuOpen ? 'Sluiten' : 'Menu') : <><span /><span /><span /></>}
        </button>

        <nav ref={menuRef} id="zs-canvas-mobile-navigation" className={`zs-nav zs-nav-${menuStyle}${menuOpen ? ' zs-nav-open' : ''}`} aria-label="Hoofdnavigatie">
          <ul>
            {navigation.map(({ page, children }) => (
              <li key={page.pageId} className={children.length ? 'zs-nav-item-has-children' : undefined}>
                {children.length ? (
                  <>
                    <button
                      type="button"
                      aria-current={page.pageId === currentPageId ? 'page' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openDropdownPageId === page.pageId}
                      style={{ cursor: 'default' }}
                      onClick={(event) => { event.stopPropagation(); setOpenDropdownPageId((current) => current === page.pageId ? null : page.pageId); }}
                    >
                      <span>{page.navigation?.label || page.title || 'Pagina'}</span>
                      <span className="zs-nav-chevron" aria-hidden="true">⌄</span>
                    </button>
                    <ul className={`zs-nav-submenu${openDropdownPageId === page.pageId ? ' zs-nav-submenu-open' : ''}`} aria-label={`${page.title || 'Pagina'} submenu`}>
                      {children.map((child) => (
                        <li key={child.pageId}>
                          <button
                            type="button"
                            aria-current={child.pageId === currentPageId ? 'page' : undefined}
                            style={{ cursor: 'default' }}
                            onClick={closeMenu}
                          >
                            {child.navigation?.label || child.title || 'Pagina'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  /* A button, not an anchor: navigating away mid-edit would
                     lose the owner's place in the Builder. */
                  <button
                    type="button"
                    aria-current={page.pageId === currentPageId ? 'page' : undefined}
                    style={{ cursor: 'default' }}
                    onClick={closeMenu}
                  >
                    {page.navigation?.label || page.title || 'Pagina'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {header.cta?.label ? <ChromeButton label={header.cta.label} theme={theme} onOpenInspector={onOpenInspector} /> : null}
      </div>
    </header>
  );
}

export function CanvasFooter({ site, pages, selected, selectedElementId, onSelect, onOpenInspector, onSelectElement, onUpdateElement, onUpdateFooter, onUpdateSettings, onOpenMedia, mediaUrl, mobile = false, viewport = mobile ? 'mobile' : 'desktop', canvasScale = 1, onMigrateFreeform, onGuidesChange }: Omit<ChromeProps, 'mobile' | 'currentPageId'> & { mobile?: boolean; viewport?: ViewportKey; selectedElementId?: string | null; onSelectElement?: (elementId: string) => void; onUpdateElement?: (elementId: string, patch: Partial<NonNullable<SiteChrome['footer']['columns']>[number]['elements'][number]>) => void; onUpdateFooter?: (patch: Partial<SiteChrome['footer']>) => void; onUpdateSettings?: (patch: Partial<SiteChrome['settings']>) => void; canvasScale?: number; onMigrateFreeform?: (placements: Record<string, FreeformGeometry>, viewport: ViewportKey) => void; onGuidesChange?: (guides: AlignmentGuide[]) => void }) {
  const suppressClickRef = useRef(false);
  // Contenteditable descendants receive native focus after pointerdown. Keep
  // that focus from opening the inspector while a direct-manipulation session
  // owns the pointer; ordinary clicks are released before their click event.
  const suppressInlineSelectionRef = useRef(false);
  const footerSurfaceRef = useRef<HTMLDivElement | null>(null);
  const [dragPreview, setDragPreview] = useState<{ elementId: string; viewport: ViewportKey; geometry: FreeformGeometry } | null>(null);
  const [footerHeightPreview, setFooterHeightPreview] = useState<number | null>(null);
  const [measuredContentBottom, setMeasuredContentBottom] = useState<number | null>(null);
  const hasV2FreeformLayout = Boolean(site?.footer.columns?.some((column) => column.elements.some((element) => element.freeformLayout?.placementVersion === 2)));
  useEffect(() => {
    if (!hasV2FreeformLayout || !footerSurfaceRef.current || !site?.footer) return;
    const surface = footerSurfaceRef.current;
    const footer = site.footer;
    const columns = footer.columns ?? [];
    const visibleElements = columns.flatMap((column) => column.elements.filter((element) => footer.showSmallText !== false || element.type !== 'copyright'));
    const visibleV2Elements = visibleElements.flatMap((element, layoutIndex) => element.freeformLayout?.placementVersion === 2 ? [{ element, layoutIndex }] : []);
    const measure = () => {
      if (!surface.isConnected) return;
      const surfaceRect = surface.getBoundingClientRect();
      const layoutReady = surfaceRect.width > 0 && surfaceRect.height > 0;
      const scale = Math.max(canvasScale, 0.01);
      let foundNode = false;
      let sawRenderableNode = false;
      let sawPositiveRect = false;
      let measuredVisibleContent = false;
      let bottom = 0;
      visibleV2Elements.forEach(({ element, layoutIndex }) => {
        const node = Array.from(surface.querySelectorAll<HTMLElement>('[data-footer-element-id]'))
          .find((candidate) => candidate.dataset.footerElementId === element.elementId);
        if (!node || !node.isConnected) return;
        foundNode = true;
        if (!node.classList.contains('zs-footer-element')) return;
        const style = getComputedStyle(node);
        const rect = node.getBoundingClientRect();
        if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number.parseFloat(style.opacity) === 0) return;
        sawRenderableNode = true;
        if (rect.width <= 0 || rect.height <= 0) return;
        sawPositiveRect = true;
        const geometry = geometryForViewport(element.freeformLayout, viewport, fallbackFreeformGeometry(layoutIndex, viewport));
        const logicalHeight = geometry.heightMode === 'fixed'
          ? geometry.height ?? FOOTER_FREEFORM_DEFAULT_ELEMENT_HEIGHT
          : rect.height / scale;
        if (!Number.isFinite(logicalHeight) || logicalHeight < 0) return;
        measuredVisibleContent = true;
        bottom = Math.max(bottom, geometry.y + logicalHeight);
      });
      // Keep the deterministic geometry estimate until the browser has
      // produced a measurable layout. This also prevents jsdom's zero rects
      // from becoming an accidental 0px content floor.
      if (!foundNode || (sawRenderableNode && !sawPositiveRect && !layoutReady)) return;
      const next = measuredVisibleContent ? bottom : 0;
      setMeasuredContentBottom((current) => current != null && Math.abs(current - next) <= FOOTER_CONTENT_MEASURE_EPSILON ? current : next);
    };
    setMeasuredContentBottom(null);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(surface);
    visibleV2Elements.forEach(({ element }) => {
      const node = surface.querySelector<HTMLElement>(`[data-footer-element-id="${element.elementId}"]`);
      if (node) observer?.observe(node);
    });
    return () => observer?.disconnect();
  }, [canvasScale, hasV2FreeformLayout, site, viewport]);
  if (!site) return null;
  const { footer, settings, theme } = site;
  const columns = footer.columns ?? [];
  const count = Math.max(1, Math.min(columns.length || Number(footer.columnLayout) || 1, 4));
  const navigation = pages.filter((page) => page.navigation?.include !== false);
  const freeform = footer.positioning === 'freeform';
  const hasCopyrightElement = columns.some((column) => column.elements.some((element) => element.type === 'copyright'));
  const footerElementCount = columns.reduce((count, column) => count + column.elements.filter((element) => footer.showSmallText !== false || element.type !== 'copyright').length, 0);
  const freeformRows = Math.max(1, Math.ceil(Math.max(footerElementCount, 1) / 3));
  const configuredMinimumHeight = configuredFooterMinimumHeight(footer.height ?? footer.minHeight);
  const visibleFooterElements = columns.flatMap((column) => column.elements.filter((element) => footer.showSmallText !== false || element.type !== 'copyright'));
  const v2EstimatedHeight = visibleFooterElements.reduce((max, element, layoutIndex) => {
    if (element.freeformLayout?.placementVersion !== 2) return max;
    const geometry = geometryForViewport(element.freeformLayout, viewport, fallbackFreeformGeometry(layoutIndex, viewport));
    const estimatedHeight = geometry.heightMode === 'fixed' ? geometry.height ?? FOOTER_FREEFORM_DEFAULT_ELEMENT_HEIGHT : FOOTER_FREEFORM_DEFAULT_ELEMENT_HEIGHT;
    return Math.max(max, geometry.y + estimatedHeight);
  }, 0);
  const freeformV2 = freeform && hasV2FreeformLayout;
  const authoredMinimumHeight = footerHeightPreview ?? configuredMinimumHeight;
  const v2ContentBottom = measuredContentBottom ?? v2EstimatedHeight;
  const v2ContentFloor = freeformV2
    ? Math.max(160, resolveFooterRenderedHeight(undefined, v2ContentBottom))
    : null;
  const freeformMinHeight = freeformV2
    ? resolveFooterRenderedHeight(authoredMinimumHeight, v2ContentBottom)
    : freeform
      ? Math.max(configuredMinimumHeight ?? 0, 160 + freeformRows * 120)
      : configuredMinimumHeight;
  const fallbackCopyright: CanvasFooterElement = {
    elementId: FOOTER_COPYRIGHT_ELEMENT_ID,
    type: 'copyright',
    heading: '',
    text: footer.smallText || `© ${new Date().getFullYear()} ${settings.siteName || 'Onze gemeente'}`,
    links: [],
    socials: [],
    logos: [],
    address: '',
    email: '',
    phone: '',
    buttonLabel: '',
    flow: 'stack',
    gap: 12,
    padding: 0,
    fontSize: footer.smallTextFontSize,
    align: footer.smallTextAlign,
  };

  const gridColumns = viewport === 'desktop' ? 12 : viewport === 'tablet' ? 8 : 4;
  type FooterElement = NonNullable<SiteChrome['footer']['columns']>[number]['elements'][number];
  const fallbackPlacement = (index: number, targetViewport: ViewportKey = 'desktop'): GridPlacement => {
    const columnsForViewport = targetViewport === 'desktop' ? 3 : targetViewport === 'tablet' ? 2 : 1;
    const span = 4;
    return { gridStart: (index % columnsForViewport) * span + 1, gridSpan: span, offsetX: 0, offsetY: Math.floor(index / columnsForViewport) * 120, layer: 'normal' };
  };
  const placementFor = (element: FooterElement, index: number, targetViewport: ViewportKey): GridPlacement => {
    const desktop = element.layout?.desktop ?? fallbackPlacement(index);
    return { ...desktop, ...(targetViewport === 'desktop' ? {} : element.layout?.[targetViewport] ?? fallbackPlacement(index, targetViewport)) };
  };
  const geometryFor = (element: FooterElement, index: number, targetViewport: ViewportKey): FreeformGeometry => {
    const fallback = fallbackFreeformGeometry(index, targetViewport);
    const geometry = geometryForViewport(element.freeformLayout, targetViewport, fallback);
    return dragPreview?.elementId === element.elementId && dragPreview.viewport === targetViewport
      ? dragPreview.geometry
      : geometry;
  };
  const layoutWithGeometry = (element: FooterElement, targetViewport: ViewportKey, geometry: FreeformGeometry) => {
    const desktop = element.freeformLayout?.desktop ?? geometry;
    return {
      ...(element.freeformLayout ?? { placementVersion: 2 as const, desktop }),
      placementVersion: 2 as const,
      desktop,
      ...(targetViewport === 'desktop' ? { desktop: geometry } : { [targetViewport]: geometry }),
    };
  };
  const legacyPlacementStyle = (element: FooterElement, index: number): React.CSSProperties => {
    const placements = (['desktop', 'tablet', 'mobile'] as ViewportKey[]).map((targetViewport) => placementFor(element, index, targetViewport));
    const column = (placement: GridPlacement, columnsForViewport: number) => `${Math.max(1, Math.min(columnsForViewport, placement.gridStart))} / span ${Math.max(1, Math.min(placement.gridSpan, columnsForViewport - Math.max(1, Math.min(columnsForViewport, placement.gridStart)) + 1))}`;
    const current = placements[viewport === 'desktop' ? 0 : viewport === 'tablet' ? 1 : 2]!;
    return {
      '--zs-footer-grid-column': column(placements[0]!, 12),
      '--zs-footer-grid-column-tablet': column(placements[1]!, 8),
      '--zs-footer-grid-column-mobile': column(placements[2]!, 4),
      '--zs-footer-offset-x': `${placements[0]!.offsetX}px`,
      '--zs-footer-offset-y': `${placements[0]!.offsetY}px`,
      '--zs-footer-offset-x-tablet': `${placements[1]!.offsetX}px`,
      '--zs-footer-offset-y-tablet': `${placements[1]!.offsetY}px`,
      '--zs-footer-offset-x-mobile': `${placements[2]!.offsetX}px`,
      '--zs-footer-offset-y-mobile': `${placements[2]!.offsetY}px`,
      zIndex: current.layer === 'foreground' ? 2 : current.layer === 'background' ? 0 : 1,
      minHeight: current.height ? `${current.height}px` : undefined,
    } as React.CSSProperties;
  };
  const freeformStyle = (element: FooterElement, index: number): React.CSSProperties => {
    const geometry = geometryFor(element, index, viewport);
    return {
      position: 'absolute',
      left: `${geometry.x}px`,
      top: `${geometry.y}px`,
      width: `${geometry.width}px`,
      height: geometry.heightMode === 'fixed' && geometry.height ? `${geometry.height}px` : 'auto',
      zIndex: layerToZIndex(geometry.layer),
      display: element.flow === 'none' ? 'block' : element.flow === 'row' ? 'flex' : 'grid',
      gap: `${element.gap ?? 12}px`,
      padding: element.padding ? `${element.padding}px` : undefined,
    };
  };

  const startManipulation = (event: React.PointerEvent, element: FooterElement, index: number, mode: 'move' | 'resize-right' | 'resize-height') => {
    if (!freeformV2 || !onUpdateElement) return;
    event.stopPropagation();
    suppressClickRef.current = false;
    const surface = footerSurfaceRef.current;
    if (!surface) return;
    suppressInlineSelectionRef.current = true;
    const target = event.currentTarget as HTMLElement;
    try { target.setPointerCapture(event.pointerId); } catch { /* pointer capture is not available in every test/browser surface */ }
    const rect = surface.getBoundingClientRect();
    const start = geometryFor(element, index, viewport);
    const elementRect = (target.closest('.zs-footer-element') as HTMLElement | null)?.getBoundingClientRect();
    const rectOf = (node: Element): GuideRect => {
      const nodeRect = node.getBoundingClientRect();
      return { left: nodeRect.left, right: nodeRect.right, top: nodeRect.top, bottom: nodeRect.bottom, width: nodeRect.width, height: nodeRect.height };
    };
    let movingGuideRect: GuideRect = elementRect
      ? { left: elementRect.left, right: elementRect.right, top: elementRect.top, bottom: elementRect.bottom, width: elementRect.width, height: elementRect.height }
      : rectOf(target);
    let surfaceGuideRect = rectOf(surface);
    let siblingRects = Array.from(surface.querySelectorAll<HTMLElement>('.zs-footer-element'))
      .filter((node) => node.dataset.footerElementId !== element.elementId)
      .filter((node) => {
        const style = getComputedStyle(node);
        return !node.hidden && style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && Number.parseFloat(style.opacity) !== 0;
      })
      .map(rectOf);
    const surfaceWidth = rect.width / Math.max(canvasScale, 0.01);
    let gridLines = footerGridGuides(surfaceWidth).map((line) => rect.left + line * Math.max(canvasScale, 0.01));
    const refreshGuideGeometry = () => {
      const currentSurface = footerSurfaceRef.current;
      const currentElement = target.closest('.zs-footer-element') as HTMLElement | null;
      if (!currentSurface || !currentElement) return;
      const currentSurfaceRect = currentSurface.getBoundingClientRect();
      surfaceGuideRect = rectOf(currentSurface);
      movingGuideRect = rectOf(currentElement);
      siblingRects = Array.from(currentSurface.querySelectorAll<HTMLElement>('.zs-footer-element'))
        .filter((node) => node.dataset.footerElementId !== element.elementId)
        .filter((node) => {
          const style = getComputedStyle(node);
          return !node.hidden && style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && Number.parseFloat(style.opacity) !== 0;
        })
        .map(rectOf);
      const currentSurfaceWidth = currentSurfaceRect.width / Math.max(canvasScale, 0.01);
      gridLines = footerGridGuides(currentSurfaceWidth).map((line) => currentSurfaceRect.left + line * Math.max(canvasScale, 0.01));
    };
    const alignmentGuidesFor = (screenDx: number, screenDy: number) => calculateAlignmentGuides({
      movingRect: movingGuideRect,
      surfaceRect: surfaceGuideRect,
      siblingRects,
      gridLines,
      delta: { x: screenDx, y: screenDy },
      scale: canvasScale,
    });
    let dragging = false;
    if (mode === 'move') onGuidesChange?.(alignmentGuidesFor(0, 0).guides);
    const guideFrame = window.requestAnimationFrame(() => {
      if (dragging) return;
      refreshGuideGeometry();
      if (mode === 'move') onGuidesChange?.(alignmentGuidesFor(0, 0).guides);
    });
    const measuredHeight = elementRect ? Math.max(40, elementRect.height / Math.max(canvasScale, 0.01)) : 96;
    const startHeight = start.heightMode === 'fixed' ? start.height ?? measuredHeight : measuredHeight;
    const startX = event.clientX;
    const startY = event.clientY;
    let latestGeometry: FreeformGeometry | null = null;
    const update = (moveEvent: PointerEvent) => {
      let screenDx = moveEvent.clientX - startX;
      let screenDy = moveEvent.clientY - startY;
      if (!dragging && Math.hypot(screenDx, screenDy) < 4) return;
      if (!dragging) refreshGuideGeometry();
      dragging = true;
      suppressClickRef.current = true;
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const alignment = mode === 'move' ? alignmentGuidesFor(screenDx, screenDy) : null;
      if (alignment) {
        screenDx = alignment.dx;
        screenDy = alignment.dy;
        onGuidesChange?.(alignment.guides);
      }
      const dx = logicalDelta(screenDx, canvasScale);
      const dy = logicalDelta(screenDy, canvasScale);
      const geometry: FreeformGeometry = mode === 'move'
        ? {
          ...start,
          x: Math.max(0, Math.min(Math.max(0, surfaceWidth - start.width), start.x + dx)),
          y: Math.max(0, start.y + dy),
        }
        : mode === 'resize-right'
          ? { ...start, width: Math.max(40, Math.min(Math.max(40, surfaceWidth - start.x), start.width + dx)) }
          : { ...start, heightMode: 'fixed', height: Math.max(40, Math.min(4000, startHeight + dy)) };
      latestGeometry = geometry;
      setDragPreview({ elementId: element.elementId, viewport, geometry });
    };
    const finish = (commit: boolean) => {
      window.cancelAnimationFrame(guideFrame);
      window.removeEventListener('pointermove', update);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      try { target.releasePointerCapture(event.pointerId); } catch { /* already released */ }
      if (commit && dragging && latestGeometry) {
        onUpdateElement(element.elementId, { freeformLayout: layoutWithGeometry(element, viewport, latestGeometry) });
        // Opening the inspector changes the available workspace and therefore
        // its fit scale. It must happen after this pointer session, never on
        // pointerdown, otherwise the drag retains an old scale while the paper
        // visibly reflows underneath it.
        if (mode === 'move') onSelectElement?.(element.elementId);
      }
      onGuidesChange?.([]);
      setDragPreview(null);
      suppressInlineSelectionRef.current = false;
      if (dragging) window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    };
    const end = () => finish(true);
    const cancel = () => finish(false);
    window.addEventListener('pointermove', update);
    window.addEventListener('pointerup', end, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
  };

  const startFooterResize = (event: React.PointerEvent) => {
    if (!freeformV2 || !onUpdateFooter) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    const startPointerY = event.clientY;
    const resizeFloor = v2ContentFloor ?? 160;
    const startMinimumHeight = Math.max(resizeFloor, Math.round(freeformMinHeight ?? resizeFloor));
    const scaleAtDragStart = Math.max(canvasScale, 0.01);
    let resizing = false;
    let latestMinimumHeight = startMinimumHeight;
    try {
      target.setPointerCapture(event.pointerId);
    } catch { /* browser fallback */ }

    const update = (moveEvent: PointerEvent) => {
      const screenDy = moveEvent.clientY - startPointerY;
      if (!resizing && Math.abs(screenDy) < 4) return;
      resizing = true;
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      latestMinimumHeight = Math.max(resizeFloor, Math.min(4000, Math.round(startMinimumHeight + screenDy / scaleAtDragStart)));
      setFooterHeightPreview(latestMinimumHeight);
    };
    const finish = (commit: boolean) => {
      window.removeEventListener('pointermove', update);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      try { target.releasePointerCapture(event.pointerId); } catch { /* already released */ }
      setFooterHeightPreview(null);
      if (commit && resizing) onUpdateFooter({ height: latestMinimumHeight });
    };
    const end = () => finish(true);
    const cancel = () => finish(false);
    window.addEventListener('pointermove', update);
    window.addEventListener('pointerup', end, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
  };

  const migrateLegacyFreeform = () => {
    if (!freeform || freeformV2 || !onMigrateFreeform || !footerSurfaceRef.current) return;
    const surface = footerSurfaceRef.current;
    const surfaceRect = surface.getBoundingClientRect();
    const placements: Record<string, FreeformGeometry> = {};
    const visibleElements = columns.flatMap((column) => column.elements.filter((element) => footer.showSmallText !== false || element.type !== 'copyright'));
    visibleElements.forEach((element) => {
      const node = Array.from(surface.querySelectorAll<HTMLElement>('[data-footer-element-id]')).find((candidate) => candidate.dataset.footerElementId === element.elementId);
      if (!node) return;
      const legacy = element.layout?.[viewport] ?? element.layout?.desktop ?? fallbackPlacement(0);
      const elementRect = node.getBoundingClientRect();
      placements[element.elementId] = legacyPlacementForMigration(legacy, elementRect, surfaceRect, canvasScale);
    });
    // The v2 contract requires a desktop base. Convert desktop first; a
    // narrow viewport may be converted independently only after that base
    // exists, so migration never guesses a desktop position.
    if (viewport !== 'desktop' && visibleElements.some((element) => !element.freeformLayout?.desktop)) return;
    if (Object.keys(placements).length) onMigrateFreeform(placements, viewport);
  };

  return (
    <footer
      className={`zs-footer ${footer.surface === 'light' ? 'zs-footer-light' : 'zs-footer-dark'} ${selected ? 'ring-1 ring-inset ring-teal-400/70' : ''}`}
      style={{
        // The editor chrome is positioned against the footer without becoming
        // part of the footer's document flow. This keeps selection stable.
        position: 'relative',
        ...(footer.backgroundColor ? { backgroundColor: footer.backgroundColor } : {}),
        ...(footer.textColor ? { color: footer.textColor } : {}),
        '--zs-footer-columns': `repeat(${count}, minmax(0, 1fr))`,
        '--zs-footer-columns-tablet': count >= 4 ? 'repeat(2, minmax(0, 1fr))' : `repeat(${Math.min(count, 3)}, minmax(0, 1fr))`,
        '--zs-canvas-footer-grid': mobile ? 'minmax(0, 1fr)' : `repeat(${count}, minmax(0, 1fr))`,
        // A footer height is a starting point, never a clipping boundary. A
        // preset may need more room for wrapped text or several elements.
        ...(freeformMinHeight ? { minHeight: `${freeformMinHeight}px` } : {}),
      } as React.CSSProperties}
      onClick={(event) => { event.stopPropagation(); onSelect(); }}
      onMouseDownCapture={(event) => {
        const target = event.target instanceof Element ? event.target : null;
        // Footer elements are focusable editor controls. Preventing the
        // browser's default focus reveal keeps the scrollable canvas under
        // the pointer when an element is clicked or dragged. Inline text
        // editing keeps native focus.
        if (event.button === 0 && target?.closest('.zs-footer-element') && !target.closest('[contenteditable="true"]')) {
          event.preventDefault();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Footer bewerken"
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(); } }}
    >
      {selected ? <div className="absolute bottom-full left-3 z-20 mb-2 flex w-fit items-center gap-1 rounded-xl border border-slate-200 bg-white p-1.5 text-slate-800 shadow-lg" onClick={(event) => event.stopPropagation()} role="toolbar" aria-label="Footer-acties"><span className="px-2 text-xs font-bold">Footer</span><button type="button" onClick={onOpenInspector} className="rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-700">Ontwerp</button>{freeform && !freeformV2 ? <button type="button" onClick={migrateLegacyFreeform} className="rounded-lg border border-teal-600 px-2.5 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-50">Legacy omzetten naar vrij vlak</button> : null}</div> : null}
      <div className={`zs-footer-inner ${freeformV2 ? 'zs-footer-inner-v2' : ''}`} style={{ ...(freeformMinHeight ? { minHeight: `${freeformMinHeight}px` } : {}), ...(freeformV2 ? { paddingBlock: 0 } : {}) }}>
        {columns.length ? (
          <div ref={footerSurfaceRef} className={`zs-footer-columns ${freeformV2 ? 'zs-footer-freeform-v2' : freeform ? 'zs-footer-freeform' : ''}`} style={{ gridTemplateColumns: freeform && !freeformV2 ? `repeat(${gridColumns}, minmax(0, 1fr))` : 'var(--zs-canvas-footer-grid)', ...(freeform && !freeformV2 ? { gridTemplateRows: `repeat(${freeformRows}, minmax(4rem, auto))` } : {}), ...(freeformV2 ? { minHeight: `${freeformMinHeight}px` } : {}) }}>
            {columns.slice(0, 4).map((column, columnIndex) => {
              const columnOffset = columns.slice(0, columnIndex).reduce((count, item) => count + item.elements.filter((element) => footer.showSmallText !== false || element.type !== 'copyright').length, 0);
              return (
              <div className={freeform ? 'contents' : 'zs-footer-column'} key={column.columnId}>
                {column.elements.filter((element) => footer.showSmallText !== false || element.type !== 'copyright').map((element, index) => {
                  const layoutIndex = columnOffset + index;
                  return (
                  <div data-footer-element-id={element.elementId} key={element.elementId} className={`zs-footer-element ${selectedElementId === element.elementId ? 'zs-footer-element-selected' : ''}`} role="button" tabIndex={0} aria-label={`${element.type} in footer bewerken`} onPointerDown={(event) => startManipulation(event, element, layoutIndex, 'move')} onClickCapture={(event) => { if (suppressClickRef.current) { event.preventDefault(); event.stopPropagation(); suppressClickRef.current = false; } }} onClick={(event) => { event.stopPropagation(); onSelectElement?.(element.elementId); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onSelectElement?.(element.elementId); } }} style={{ ...(freeformV2 ? freeformStyle(element, layoutIndex) : freeform ? legacyPlacementStyle(element, layoutIndex) : { display: element.flow === 'none' ? 'block' : element.flow === 'row' ? 'flex' : 'grid', gap: `${element.gap ?? 12}px`, padding: element.padding ? `${element.padding}px` : undefined }), textAlign: element.align ?? 'left', ...(element.fontSize ? { fontSize: `${element.fontSize}px` } : {}) }}>
                    <FooterElementPreview
                      element={element}
                      site={site}
                      navigation={navigation}
                      theme={theme}
                      editable
                      onSelect={() => { if (!suppressInlineSelectionRef.current) onSelectElement?.(element.elementId); }}
                      onOpenMedia={onOpenMedia}
                      mediaUrl={mediaUrl}
                      onUpdate={(patch) => onUpdateElement?.(element.elementId, patch)}
                    />
                    {freeformV2 && selectedElementId === element.elementId ? <><button type="button" aria-label="Footeronderdeel breder of smaller maken" className="zs-footer-resize-right" onPointerDown={(event) => startManipulation(event, element, layoutIndex, 'resize-right')} /><button type="button" aria-label="Hoogte van footeronderdeel aanpassen" className="zs-footer-resize-bottom" onPointerDown={(event) => startManipulation(event, element, layoutIndex, 'resize-height')} /></> : null}
                  </div>
                  );
                })}
              </div>
              );
            })}
            {freeformV2 ? <button type="button" data-testid="footer-resize-handle" aria-label="Hoogte van footer aanpassen" className="zs-footer-surface-resize-bottom" onPointerDown={startFooterResize} /> : null}
          </div>
        ) : (
          <div className="zs-footer-column">
            {footer.showSiteName !== false ? <EditableFooterText as="strong" value={settings.siteName || 'Onze gemeente'} editable onChange={(siteName) => onUpdateSettings?.({ siteName })} /> : null}
            {footer.description ? <EditableFooterText value={footer.description} editable style={{ ...(footer.descriptionFontSize ? { fontSize: `${footer.descriptionFontSize}px` } : {}), textAlign: footer.descriptionAlign ?? 'left' }} onChange={(description) => onUpdateFooter?.({ description })} /> : null}
          </div>
        )}
        {footer.showSmallText !== false && !hasCopyrightElement ? (
          <div
            className={`zs-footer-bottom ${selectedElementId === FOOTER_COPYRIGHT_ELEMENT_ID ? 'zs-footer-element-selected' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="Copyright in footer bewerken"
            onPointerDown={(event) => { event.stopPropagation(); onSelectElement?.(FOOTER_COPYRIGHT_ELEMENT_ID); }}
            onClick={(event) => { event.stopPropagation(); onSelectElement?.(FOOTER_COPYRIGHT_ELEMENT_ID); }}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onSelectElement?.(FOOTER_COPYRIGHT_ELEMENT_ID); } }}
          >
            <FooterElementPreview
              element={fallbackCopyright}
              site={site}
              navigation={navigation}
              theme={theme}
              editable
              onSelect={() => onSelectElement?.(FOOTER_COPYRIGHT_ELEMENT_ID)}
              onUpdate={(patch) => onUpdateElement?.(FOOTER_COPYRIGHT_ELEMENT_ID, patch)}
            />
          </div>
        ) : null}
      </div>
    </footer>
  );
}

function FooterElementPreview({
  element,
  site,
  navigation,
  theme,
  editable = false,
  onSelect,
  onOpenMedia,
  mediaUrl,
  onUpdate,
}: {
  element: NonNullable<SiteChrome['footer']['columns']>[number]['elements'][number];
  site: SiteChrome;
  navigation: SitePage[];
  theme: Theme;
  editable?: boolean;
  onSelect?: () => void;
  onOpenMedia?: (target: MediaTarget) => void;
  mediaUrl?: (assetId: string) => string;
  onUpdate?: (patch: Partial<NonNullable<SiteChrome['footer']['columns']>[number]['elements'][number]>) => void;
}) {
  const heading = element.heading ? <EditableFooterText value={element.heading} className="zs-footer-heading" editable={editable} onSelect={onSelect} onChange={(heading) => onUpdate?.({ heading })} /> : null;

  switch (element.type) {
    case 'logo':
      return (
        <>
          <EditableFooterText as="strong" value={element.heading || site.settings.siteName || 'Onze gemeente'} editable={editable} onSelect={onSelect} onChange={(heading) => onUpdate?.({ heading })} />
          {element.text ? <EditableFooterText value={element.text} editable={editable} onSelect={onSelect} onChange={(text) => onUpdate?.({ text })} /> : null}
        </>
      );
    case 'text':
      return (<>{heading}<EditableFooterText value={element.text} editable={editable} onSelect={onSelect} onChange={(text) => onUpdate?.({ text })} /></>);
    case 'links':
    case 'legal': {
      const items = element.links.length
        ? element.links.map((link) => link.label).filter(Boolean)
        : navigation.map((page) => page.navigation?.label || page.title);
      return (
        <>
          {heading}
          <ul className="zs-footer-links">
            {items.map((label, index) => <li key={index}><span style={{ cursor: 'default' }}>{label}</span></li>)}
          </ul>
        </>
      );
    }
    case 'contact':
      return (
        <>
          {heading}
          <address className="zs-footer-contact">
            {element.address ? <EditableFooterText as="span" value={element.address} editable={editable} onSelect={onSelect} onChange={(address) => onUpdate?.({ address })} /> : null}
            {element.email ? <EditableFooterText as="span" value={element.email} editable={editable} onSelect={onSelect} onChange={(email) => onUpdate?.({ email })} /> : null}
            {element.phone ? <EditableFooterText as="span" value={element.phone} editable={editable} onSelect={onSelect} onChange={(phone) => onUpdate?.({ phone })} /> : null}
          </address>
        </>
      );
    case 'socials':
      return (
        <>
          {heading}
          <div className="zs-socials">
            {element.socials.map((social, index) => (
              <span className="zs-social" key={index} title={social.platform}>
                <SocialMark platform={social.platform} />
              </span>
            ))}
          </div>
        </>
      );
    case 'partner_logos':
      return (
        <>
          {heading}
          <div className="zs-logo-wall">
            {element.logos.map((logo, index) => (
              <button type="button" className="zs-logo-placeholder zs-logo-image-button cursor-pointer" key={index} aria-label={`${logo.name || 'Logo'} wijzigen`} onClick={(event) => { event.stopPropagation(); onSelect?.(); onOpenMedia?.({ kind: 'footer-logo', elementId: element.elementId, index }); }}>{logo.assetId && mediaUrl ? <img src={mediaUrl(logo.assetId)} alt={logo.name || 'Logo'} className="zs-logo-image" /> : logo.name || 'Logo'}</button>
            ))}
          </div>
        </>
      );
    case 'button':
      return (<>{heading}<div><ChromeButton label={element.buttonLabel || 'Meer informatie'} theme={theme} onOpenInspector={onSelect} /></div></>);
    case 'cookie_preferences':
      return (<>{heading}<EditableFooterText value={element.text || 'Cookievoorkeuren'} editable={editable} onSelect={onSelect} onChange={(text) => onUpdate?.({ text })} /></>);
    case 'copyright':
      return <EditableFooterText value={element.text} editable={editable} onSelect={onSelect} onChange={(text) => onUpdate?.({ text })} />;
    case 'spacer':
      return <div className="zs-footer-spacer" aria-hidden="true" style={{ minHeight: `${element.gap ?? 24}px` }} />;
    case 'divider':
      return <hr className="zs-footer-divider" />;
    case 'shape':
      return <div className="zs-shape zs-footer-shape" aria-hidden="true" style={resolveVisualSurface(theme, element.shape ?? {}) as React.CSSProperties} />;
    default:
      return null;
  }
}

function EditableFooterText({
  value,
  onChange,
  editable,
  onSelect,
  className,
  as = 'p',
  style,
}: {
  value: string;
  onChange: (value: string) => void;
  editable: boolean;
  onSelect?: () => void;
  className?: string;
  as?: 'p' | 'span' | 'strong';
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.textContent !== value) ref.current.textContent = value;
  }, [value]);
  const Tag = as;
  return (
    <Tag
      ref={ref as never}
      className={className}
      style={style}
      contentEditable={editable}
      suppressContentEditableWarning
      role={editable ? 'textbox' : undefined}
      onInput={(event) => onChange(event.currentTarget.textContent ?? '')}
      onBlur={(event) => onChange(event.currentTarget.textContent ?? '')}
      onFocus={() => { if (editable) onSelect?.(); }}
      onClick={(event) => { if (editable) { onSelect?.(); event.stopPropagation(); } }}
    >
      {value}
    </Tag>
  );
}

/** A button inside site chrome. Same tokens and classes as a content button;
 * never a link, for the same reason. */
function ChromeButton({ label, theme, onOpenInspector }: { label: string; theme: Theme; onOpenInspector?: () => void }) {
  const resolved = resolveButton(theme, { style: 'primary', size: 'small' });
  return (
    <span role="button" tabIndex={0} aria-label="Headerknop bewerken" className="zs-button zs-button-primary cursor-pointer" style={resolved.variables as React.CSSProperties} onClick={(event) => { event.stopPropagation(); onOpenInspector?.(); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onOpenInspector?.(); } }}>
      <span>{label}</span>
    </span>
  );
}

/** Mirror of the renderer's social marks, so the canvas shows real icons
 * rather than a placeholder square. */
const SOCIAL_PATHS: Record<string, string> = {
  facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z',
  instagram: 'M17 2H7a5 5 0 0 0-5 5v10a5 5 0 0 0 5 5h10a5 5 0 0 0 5-5V7a5 5 0 0 0-5-5zM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7zM17.5 6.5h.01',
  youtube: 'M22.5 6.4a2.8 2.8 0 0 0-2-2C18.7 4 12 4 12 4s-6.7 0-8.5.4a2.8 2.8 0 0 0-2 2A29 29 0 0 0 1.2 12a29 29 0 0 0 .3 5.6 2.8 2.8 0 0 0 2 2C5.3 20 12 20 12 20s6.7 0 8.5-.4a2.8 2.8 0 0 0 2-2 29 29 0 0 0 .3-5.6 29 29 0 0 0-.3-5.6zM9.8 15.4V8.6l5.7 3.4z',
  linkedin: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6zM6 9H2v12h4zM4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  x: 'M4 4l16 16M20 4L4 20',
  tiktok: 'M21 8.5a6.5 6.5 0 0 1-5-2.4V15a6 6 0 1 1-6-6c.3 0 .7 0 1 .1v3.2a2.8 2.8 0 1 0 2 2.7V2h3a6.5 6.5 0 0 0 5 5.5z',
  spotify: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.5 14.4a.8.8 0 0 1-1 .3 11 11 0 0 0-6.9-1 .8.8 0 0 1-.3-1.5 12.6 12.6 0 0 1 7.9 1.2.8.8 0 0 1 .3 1zm1.2-3a1 1 0 0 1-1.3.3 14 14 0 0 0-8.4-1.1 1 1 0 1 1-.4-1.9 16 16 0 0 1 9.7 1.3 1 1 0 0 1 .4 1.4zm.1-3.1a17 17 0 0 0-9.9-1.3 1.2 1.2 0 1 1-.5-2.3 19 19 0 0 1 11.3 1.5 1.2 1.2 0 0 1-1 2.1z',
};

export function SocialMark({ platform }: { platform: string }) {
  const path = SOCIAL_PATHS[platform];
  if (!path) return null;
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

export const SOCIAL_PLATFORMS = Object.keys(SOCIAL_PATHS);
export { resolveDestination, CanvasIcon };
